/**
 * BoardPage - Main Kanban board page
 */

import { useEffect, useState, useCallback, useRef, useDeferredValue } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { clsx } from 'clsx';
import { Settings, Filter, Search, Files, StickyNote, ChevronRight, Archive } from 'lucide-react';
import { useBoardStore } from '@stores/boardStore';
import { useBoardPanelsStore } from '@stores/boardPanelsStore';
import { useProjectStore } from '@stores/projectStore';
import { useSubscriptionStore } from '@stores/subscriptionStore';
import { tasksApi } from '@services/api';
import { UpgradePrompt } from '@/components/subscription';
import {
  KanbanBoard,
  TaskDetailModal,
  TaskForm,
  ListView,
  TimelineView,
  CalendarView,
  TableView,
  FilesView,
  ViewSwitcher,
  ColumnSettingsModal,
  AddColumnModal,
  BoardSettingsModal,
  type BoardViewType,
} from '@components/board';
import type { BoardColumn } from '@services/api';
import LoadingSpinner from '@components/ui/LoadingSpinner';
import { toast } from '@components/ui/Toast';
import { isApiError } from '@services/api/client';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { BoardFilesPanel, BoardNotesPanel } from '@/components/board/panels';

// ViewType is now imported from ViewSwitcher as BoardViewType

export default function BoardPage() {
  const { t } = useTranslation();
  const { boardId } = useParams<{ boardId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const boardContainerRef = useRef<HTMLDivElement>(null);

  const {
    currentBoard,
    isLoading,
    error,
    fetchBoardTasks,
    fetchBoardWithTasks,
    selectTask,
    restoreBoard,
  } = useBoardStore();
  const [isRestoring, setIsRestoring] = useState(false);

  const handleRestoreBoard = async () => {
    if (!currentBoard) return;
    setIsRestoring(true);
    try {
      await restoreBoard(currentBoard.id);
      toast.success(t('board.boardRestored'));
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : t('board.failedRestoreBoard'));
    } finally {
      setIsRestoring(false);
    }
  };

  // Board panels (files & notes)
  const {
    leftPanelOpen,
    rightPanelOpen,
    toggleLeftPanel,
    toggleRightPanel,
    setBoardContext,
    clearBoardContext,
  } = useBoardPanelsStore();

  // Subscription data for upgrade prompts
  const { usage, currentSubscription, fetchCurrentSubscription } = useSubscriptionStore();

  // Parent project (for breadcrumb / navigation back up the hierarchy)
  const { currentProject, projects, fetchProject } = useProjectStore();
  const projectId = currentBoard?.projectId;
  const project = projectId
    ? currentProject?.id === projectId
      ? currentProject
      : (projects.find((p) => p.id === projectId) ?? null)
    : null;

  const [viewType, setViewType] = useState<BoardViewType>('kanban');
  const [searchQuery, setSearchQuery] = useState('');
  // Defer the value used to filter/render tasks so typing stays responsive on
  // large boards (raw value stays in the input for immediate feedback).
  const deferredSearchQuery = useDeferredValue(searchQuery);
  const [showMobileSearch, setShowMobileSearch] = useState(false);
  const [showTaskForm, setShowTaskForm] = useState(false);
  const [selectedColumnId, setSelectedColumnId] = useState<string | null>(null);

  // Filter state
  const [showFilterMenu, setShowFilterMenu] = useState(false);
  const [filterPriority, setFilterPriority] = useState<string>('');
  const [filterColumnId, setFilterColumnId] = useState<string>('');
  const filterMenuRef = useRef<HTMLDivElement>(null);

  // Column management state
  const [showAddColumn, setShowAddColumn] = useState(false);
  const [showColumnSettings, setShowColumnSettings] = useState(false);
  const [selectedColumn, setSelectedColumn] = useState<BoardColumn | null>(null);

  // Board settings state
  const [showBoardSettings, setShowBoardSettings] = useState(false);

  // Delete task confirmation state
  const [showDeleteTaskConfirm, setShowDeleteTaskConfirm] = useState(false);
  const [deletingTaskId, setDeletingTaskId] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Close filter menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (filterMenuRef.current && !filterMenuRef.current.contains(event.target as Node)) {
        setShowFilterMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Check if any filter is active
  const hasActiveFilters = filterPriority || filterColumnId;

  // Fetch board and tasks on mount - use atomic loading to prevent race conditions
  useEffect(() => {
    if (boardId) {
      void fetchBoardWithTasks(boardId);
      // Fetch subscription data for upgrade prompts
      fetchCurrentSubscription().catch((err) => {
        // Silently fail if subscription data unavailable
        console.warn('Failed to fetch subscription data:', err);
      });
    }
  }, [boardId, fetchBoardWithTasks, fetchCurrentSubscription]);

  // Ensure the parent project is loaded for the breadcrumb, even on a deep link
  // (board opened directly from search / AI chat / shared ?task= URL).
  useEffect(() => {
    if (projectId && !project) {
      void fetchProject(projectId);
    }
  }, [projectId, project, fetchProject]);

  // The URL owns the selection: browser Back/Forward and direct links all
  // use the same state, including the absence of a task parameter.
  const selectedTaskId = currentBoard?.id === boardId ? searchParams.get('task') : null;
  useEffect(() => { selectTask(selectedTaskId); }, [selectedTaskId, selectTask]);

  // Set board context for panels
  useEffect(() => {
    if (boardId) {
      setBoardContext(boardId);
    }
    return () => {
      clearBoardContext();
    };
  }, [boardId, setBoardContext, clearBoardContext]);

  const clearTaskParam = useCallback(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('task');
        return next;
      },
      { replace: true }
    );
  }, [setSearchParams]);

  const handleTaskClick = useCallback(
    (taskId: string) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set('task', taskId);
        return next;
      });
    },
    [setSearchParams]
  );

  const handleTaskClose = useCallback(() => {
    selectTask(null);
    clearTaskParam();
    // Refresh board tasks to reflect any changes made in the modal
    if (boardId) {
      void fetchBoardTasks(boardId).catch(() => toast.error(t('board.unableToLoad')));
    }
  }, [selectTask, clearTaskParam, boardId, fetchBoardTasks, t]);

  const handleAddTask = useCallback((columnId: string) => {
    setSelectedColumnId(columnId);
    setShowTaskForm(true);
  }, []);

  const handleCreateTask = useCallback(
    async (data: any) => {
      if (!boardId || !selectedColumnId) return;

      try {
        await tasksApi.create({
          boardId,
          columnId: selectedColumnId,
          ...data,
        });

        // Refresh board tasks to get enriched data (assignee, subtask counts, etc.)
        await fetchBoardTasks(boardId);

        setShowTaskForm(false);
        setSelectedColumnId(null);
        toast.success(t('board.taskCreated'));
      } catch (error) {
        console.error('Failed to create task:', error);
        if (isApiError(error)) {
          toast.error(error.message || t('board.taskFailedCreate'));
        } else {
          toast.error(t('board.taskFailedCreate'));
        }
        // Rethrow so TaskForm keeps the modal open with the user's input intact
        // instead of resetting and closing on a failed submit.
        throw error;
      }
    },
    [boardId, selectedColumnId, fetchBoardTasks, t]
  );

  const handleDeleteTask = useCallback((taskId: string) => {
    setDeletingTaskId(taskId);
    setShowDeleteTaskConfirm(true);
  }, []);

  const handleConfirmDeleteTask = useCallback(async () => {
    if (!deletingTaskId) return;

    setIsDeleting(true);

    try {
      await tasksApi.delete(deletingTaskId);
      const { removeTask } = useBoardStore.getState();
      removeTask(deletingTaskId);
      selectTask(null);
      clearTaskParam();
      toast.success(t('board.taskDeleted'));
      setShowDeleteTaskConfirm(false);
      setDeletingTaskId(null);
    } catch (error) {
      console.error('Failed to delete task:', error);
      if (isApiError(error)) {
        if (error.status === 409 || error.code === 'CONFLICT') {
          toast.warning(t('board.taskModifiedByOther'));
        } else if (error.status === 404) {
          // Task was already deleted
          const { removeTask } = useBoardStore.getState();
          removeTask(deletingTaskId);
          selectTask(null);
          clearTaskParam();
          toast.info(t('board.taskAlreadyDeleted'));
          setShowDeleteTaskConfirm(false);
          setDeletingTaskId(null);
        } else {
          toast.error(error.message || t('board.taskFailedDelete'));
        }
      } else {
        toast.error(t('board.taskFailedDelete'));
      }
    } finally {
      setIsDeleting(false);
    }
  }, [deletingTaskId, selectTask, clearTaskParam, t]);

  // Column handlers
  const handleColumnSettings = useCallback(
    (columnId: string) => {
      const column = currentBoard?.columns.find((c) => c.id === columnId);
      if (column) {
        setSelectedColumn(column);
        setShowColumnSettings(true);
      }
    },
    [currentBoard?.columns]
  );

  const handleCloseColumnSettings = useCallback(() => {
    setShowColumnSettings(false);
    setSelectedColumn(null);
  }, []);

  const handleAddColumn = useCallback(() => {
    setShowAddColumn(true);
  }, []);

  const handleCloseAddColumn = useCallback(() => {
    setShowAddColumn(false);
  }, []);

  // Get task count for selected column (for delete validation)
  const { columnTaskIds } = useBoardStore.getState();
  const selectedColumnTaskCount = selectedColumn
    ? columnTaskIds[selectedColumn.id]?.length || 0
    : 0;

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-neutral-50 dark:bg-neutral-950">
        <div className="flex flex-col items-center gap-4">
          <LoadingSpinner size="lg" className="text-primary-600 dark:text-primary-400 animate-float" />
          <p className="text-sm font-medium text-neutral-600 dark:text-neutral-400 animate-pulse-subtle">{t('board.loadingBoard')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-screen items-center justify-center bg-neutral-50 dark:bg-neutral-950">
        <div className="text-center animate-fade-in">
          <div className="mb-4 inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-accent-50/80 dark:bg-accent-950/30">
            <svg className="w-8 h-8 text-accent-600 dark:text-accent-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <p className="text-lg font-semibold text-neutral-900 dark:text-white mb-2">{t('board.unableToLoad')}</p>
          <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-6">{error}</p>
          <button
            onClick={() => boardId && void fetchBoardWithTasks(boardId)}
            className="px-6 py-3 rounded-xl bg-primary-600 text-white font-medium hover:bg-primary-700 shadow-soft hover:shadow-glow transition-all duration-200 button-hover-lift"
          >
            {t('board.tryAgain')}
          </button>
        </div>
      </div>
    );
  }

  if (!currentBoard) {
    return (
      <div className="flex h-screen items-center justify-center bg-neutral-50 dark:bg-neutral-950">
        <div className="text-center animate-fade-in">
          <div className="mb-4 inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-neutral-100 dark:bg-neutral-800">
            <svg className="w-8 h-8 text-neutral-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <p className="text-lg font-semibold text-neutral-900 dark:text-white">{t('board.notFound')}</p>
          <p className="mt-2 text-sm text-neutral-600 dark:text-neutral-400">{t('board.notFoundDescription')}</p>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={boardContainerRef}
      className="workspace-surface relative flex h-[calc(100dvh-4rem)] flex-col bg-neutral-50 dark:bg-neutral-950"
    >
      {/* Breadcrumb - links back up the Projects > Project > Board hierarchy */}
      <nav
        aria-label="Breadcrumb"
        className="flex-shrink-0 border-b border-neutral-200/50 dark:border-neutral-800/50 bg-white dark:bg-neutral-900 px-3 sm:px-4 md:px-6 py-2"
      >
        <ol className="flex items-center gap-1.5 text-xs sm:text-sm text-neutral-500 dark:text-neutral-400 min-w-0">
          <li className="flex-shrink-0">
            <Link
              to="/projects"
              className="hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
            >
              {t('nav.projects')}
            </Link>
          </li>
          {project && (
            <>
              <li className="flex-shrink-0" aria-hidden="true">
                <ChevronRight className="h-3.5 w-3.5 text-neutral-400" />
              </li>
              <li className="min-w-0">
                <Link
                  to={`/projects/${project.id}`}
                  className="block truncate hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                >
                  {project.name}
                </Link>
              </li>
            </>
          )}
          <li className="flex-shrink-0" aria-hidden="true">
            <ChevronRight className="h-3.5 w-3.5 text-neutral-400" />
          </li>
          <li
            className="min-w-0 truncate font-medium text-neutral-700 dark:text-neutral-300"
            aria-current="page"
          >
            {currentBoard.name}
          </li>
        </ol>
      </nav>

      {currentBoard.isArchived && (
        <div
          role="status"
          className="flex flex-shrink-0 flex-wrap items-center gap-3 border-b border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-900/50 dark:bg-amber-900/20 dark:text-amber-200 sm:px-4 md:px-6"
        >
          <Archive className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
          <span className="min-w-0 flex-1">{t('board.archivedBanner')}</span>
          <button
            type="button"
            onClick={handleRestoreBoard}
            disabled={isRestoring}
            className="rounded-md bg-amber-600 px-3 py-1 text-xs font-medium text-white hover:bg-amber-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {t('board.restoreBoard')}
          </button>
        </div>
      )}

      {/* Header */}
      <header className="flex-shrink-0 border-b border-neutral-200/50 dark:border-neutral-800/50 bg-white dark:bg-neutral-900 px-3 sm:px-4 md:px-6 py-3 sm:py-4">
        <div className="flex flex-col gap-3">
          <div className="flex items-center gap-2 sm:gap-3 md:gap-4 min-w-0 w-full">
            {/* Files panel toggle - hidden on mobile */}
            <button
              onClick={toggleLeftPanel}
              className={clsx(
                'flex rounded-xl p-2 sm:p-2.5 transition-all duration-200 button-hover-lift flex-shrink-0',
                leftPanelOpen
                  ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300 shadow-glow'
                  : 'text-neutral-600 hover:bg-primary-50/80 hover:text-primary-700 dark:text-neutral-400 dark:hover:bg-primary-950/30 dark:hover:text-primary-300'
              )}
              title={t('board.boardFiles')}
            >
              <Files className="h-5 w-5" />
            </button>

            <div className="min-w-0 flex-1">
              <h1 className="text-lg sm:text-xl md:text-2xl font-sans font-semibold tracking-tight text-neutral-900 dark:text-white truncate">
                {currentBoard.name}
              </h1>
              {currentBoard.description && (
                <p className="hidden sm:block mt-1 md:mt-1.5 text-xs sm:text-sm text-neutral-600 dark:text-neutral-400 max-w-2xl truncate">
                  {currentBoard.description}
                </p>
              )}
            </div>
          </div>

          <div className="flex w-full items-center gap-2">
            {/* Search */}
            <div className="relative order-2 ml-auto hidden lg:block">
              <Search className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-500 dark:text-neutral-400" />
              <input
                type="text"
                aria-label={t('board.searchTasks')}
                placeholder={t('board.searchTasks')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={clsx(
                  'w-48 lg:w-64 rounded-xl border border-neutral-200/80 py-2.5 pl-10 pr-4',
                  'bg-neutral-50/50 text-sm text-neutral-900',
                  'dark:border-neutral-700/50 dark:bg-neutral-800/50 dark:text-white',
                  'placeholder:text-neutral-500 dark:placeholder:text-neutral-400',
                  'focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-500/20',
                  'transition-all duration-200'
                )}
              />
            </div>

            {/* Mobile search toggle - expands an inline input below the header */}
            <button
              onClick={() => setShowMobileSearch((v) => !v)}
              aria-expanded={showMobileSearch}
              className={clsx(
                'lg:hidden rounded-lg sm:rounded-xl p-2 sm:p-2.5 transition-all duration-200 active:scale-95',
                searchQuery || showMobileSearch
                  ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300'
                  : 'text-neutral-600 hover:bg-primary-50/80 hover:text-primary-700 dark:text-neutral-400 dark:hover:bg-primary-950/30 dark:hover:text-primary-300'
              )}
              title={t('common.search')}
            >
              <Search className="h-5 w-5" />
            </button>

            {/* View switcher */}
            <ViewSwitcher currentView={viewType} onViewChange={setViewType} />

            {/* Filter & Settings */}
            <div className="relative order-3" ref={filterMenuRef}>
              <button
                onClick={() => setShowFilterMenu(!showFilterMenu)}
                aria-expanded={showFilterMenu}
                aria-label={t('common.filters')}
                className={clsx(
                  'rounded-lg sm:rounded-xl p-2 sm:p-2.5 relative transition-all duration-200 active:scale-95',
                  hasActiveFilters
                    ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300'
                    : 'text-neutral-600 hover:bg-primary-50/80 hover:text-primary-700 dark:text-neutral-400 dark:hover:bg-primary-950/30 dark:hover:text-primary-300'
                )}
                title={t('common.filters')}
              >
                <Filter className="h-5 w-5" />
                {hasActiveFilters && (
                  <span className="absolute -top-0.5 sm:-top-1 -right-0.5 sm:-right-1 h-2.5 w-2.5 sm:h-3 sm:w-3 bg-primary-600 rounded-full animate-pulse-subtle ring-1 sm:ring-2 ring-white dark:ring-neutral-900" />
                )}
              </button>

              {showFilterMenu && (
                <div className="absolute right-0 top-full mt-2 sm:mt-3 z-50 w-64 sm:w-72 bg-white dark:bg-neutral-900 border border-neutral-200/50 dark:border-neutral-700/50 rounded-xl sm:rounded-2xl shadow-soft-lg p-4 sm:p-5 animate-scale-in">
                  <div className="space-y-4 sm:space-y-5">
                    <div>
                      <label className="block text-xs sm:text-sm font-semibold text-neutral-800 dark:text-neutral-200 mb-1.5 sm:mb-2">
                        {t('board.filterPriority')}
                      </label>
                      <select
                        value={filterPriority}
                        onChange={(e) => setFilterPriority(e.target.value)}
                        className={clsx(
                          'w-full px-3 sm:px-3.5 py-2.5 sm:py-3 border border-neutral-200/80 dark:border-neutral-600/50 rounded-lg sm:rounded-xl text-sm',
                          'bg-neutral-50/50 dark:bg-neutral-700/50 text-neutral-900 dark:text-white',
                          'focus:border-primary-400 focus:ring-2 focus:ring-primary-500/20',
                          'transition-all duration-200'
                        )}
                      >
                        <option value="">{t('board.allPriorities')}</option>
                        <option value="urgent">{t('task.priorities.urgent')}</option>
                        <option value="high">{t('task.priorities.high')}</option>
                        <option value="medium">{t('task.priorities.medium')}</option>
                        <option value="low">{t('task.priorities.low')}</option>
                        <option value="none">{t('task.priorities.none')}</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-xs sm:text-sm font-semibold text-neutral-800 dark:text-neutral-200 mb-1.5 sm:mb-2">
                        {t('board.filterColumn')}
                      </label>
                      <select
                        value={filterColumnId}
                        onChange={(e) => setFilterColumnId(e.target.value)}
                        className={clsx(
                          'w-full px-3 sm:px-3.5 py-2.5 sm:py-3 border border-neutral-200/80 dark:border-neutral-600/50 rounded-lg sm:rounded-xl text-sm',
                          'bg-neutral-50/50 dark:bg-neutral-700/50 text-neutral-900 dark:text-white',
                          'focus:border-primary-400 focus:ring-2 focus:ring-primary-500/20',
                          'transition-all duration-200'
                        )}
                      >
                        <option value="">{t('board.allColumns')}</option>
                        {currentBoard?.columns?.map((col) => (
                          <option key={col.id} value={col.id}>
                            {col.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    {hasActiveFilters && (
                      <button
                        onClick={() => {
                          setFilterPriority('');
                          setFilterColumnId('');
                        }}
                        className="w-full px-3 sm:px-4 py-3 sm:py-2.5 text-sm font-medium text-primary-600 dark:text-primary-400 hover:bg-primary-50/80 dark:hover:bg-primary-900/30 rounded-lg sm:rounded-xl transition-all duration-200 active:scale-95"
                      >
                        {t('board.clearFilters')}
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
            <button
              onClick={() => setShowBoardSettings(true)}
              className={clsx(
                'order-3 flex rounded-lg sm:rounded-xl p-2 sm:p-2.5 transition-all duration-200 active:scale-95 animate-fade-in stagger-4',
                'text-neutral-600 hover:bg-primary-50/80 hover:text-primary-700',
                'dark:text-neutral-400 dark:hover:bg-primary-950/30 dark:hover:text-primary-300'
              )}
              title={t('board.boardSettings')}
            >
              <Settings className="h-5 w-5" />
            </button>

            {/* Notes panel toggle - hidden on mobile */}
            <button
              onClick={toggleRightPanel}
              className={clsx(
                'order-3 flex rounded-lg p-2.5 transition-colors',
                rightPanelOpen
                  ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300'
                  : 'text-neutral-600 hover:bg-primary-50/80 hover:text-primary-700 dark:text-neutral-400 dark:hover:bg-primary-950/30 dark:hover:text-primary-300'
              )}
              title={t('board.boardNotes')}
            >
              <StickyNote className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Mobile inline search - expands full-width under the header row */}
        {showMobileSearch && (
          <div className="lg:hidden relative mt-3 animate-slide-down">
            <Search className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-500 dark:text-neutral-400" />
            <input
              type="text"
              autoFocus
              aria-label={t('board.searchTasks')}
                placeholder={t('board.searchTasks')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={clsx(
                'w-full rounded-xl border border-neutral-200/80 py-2.5 pl-10 pr-4',
                'bg-neutral-50/50 text-sm text-neutral-900',
                'dark:border-neutral-700/50 dark:bg-neutral-800/50 dark:text-white',
                'placeholder:text-neutral-500 dark:placeholder:text-neutral-400',
                'focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-500/20',
                'transition-all duration-200'
              )}
            />
          </div>
        )}
      </header>

      {/* Board content */}
      <main
        className={clsx(
          'flex-1 overflow-hidden transition-all duration-200',
          // Panels act as overlays below md (they render their own backdrop),
          // so only reserve margin once they dock as sidebars at md+.
          leftPanelOpen && 'md:ml-80',
          rightPanelOpen && 'md:mr-80'
        )}
      >
        {/* Upgrade prompt when approaching limits */}
        {usage && (
          <div className="px-4 pt-4">
            <UpgradePrompt
              usage={usage}
              {...(currentSubscription?.plan.name && { planName: currentSubscription.plan.name })}
            />
          </div>
        )}

        {viewType === 'kanban' && (
          <KanbanBoard
            onTaskClick={handleTaskClick}
            onAddTask={handleAddTask}
            onAddColumn={handleAddColumn}
            onColumnSettings={handleColumnSettings}
            searchQuery={deferredSearchQuery}
            filterPriority={filterPriority}
            filterColumnId={filterColumnId}
          />
        )}
        {viewType === 'list' && (
          <ListView
            onTaskClick={handleTaskClick}
            searchQuery={deferredSearchQuery}
            filterPriority={filterPriority}
            filterColumnId={filterColumnId}
          />
        )}
        {viewType === 'timeline' && (
          <TimelineView
            onTaskClick={handleTaskClick}
            searchQuery={deferredSearchQuery}
            filterPriority={filterPriority}
            filterColumnId={filterColumnId}
          />
        )}
        {viewType === 'calendar' && (
          <CalendarView
            onTaskClick={handleTaskClick}
            searchQuery={deferredSearchQuery}
            filterPriority={filterPriority}
            filterColumnId={filterColumnId}
          />
        )}
        {viewType === 'table' && (
          <TableView
            onTaskClick={handleTaskClick}
            searchQuery={deferredSearchQuery}
            filterPriority={filterPriority}
            filterColumnId={filterColumnId}
          />
        )}
        {viewType === 'files' && (
          <FilesView searchQuery={deferredSearchQuery} />
        )}
      </main>

      {/* Task detail modal */}
      <TaskDetailModal
        taskId={selectedTaskId}
        open={!!selectedTaskId}
        onClose={handleTaskClose}
        onDelete={handleDeleteTask}
      />

      {/* Create task form */}
      <TaskForm
        open={showTaskForm}
        onClose={() => {
          setShowTaskForm(false);
          setSelectedColumnId(null);
        }}
        onSubmit={handleCreateTask}
      />

      {/* Column settings modal */}
      <ColumnSettingsModal
        column={selectedColumn}
        boardId={boardId || ''}
        taskCount={selectedColumnTaskCount}
        open={showColumnSettings}
        onClose={handleCloseColumnSettings}
      />

      {/* Add column modal */}
      <AddColumnModal
        boardId={boardId || ''}
        open={showAddColumn}
        onClose={handleCloseAddColumn}
      />

      {/* Board settings modal */}
      <BoardSettingsModal
        board={currentBoard}
        open={showBoardSettings}
        onClose={() => setShowBoardSettings(false)}
      />

      {/* Delete task confirmation dialog */}
      <ConfirmDialog
        open={showDeleteTaskConfirm}
        onClose={() => {
          setShowDeleteTaskConfirm(false);
          setDeletingTaskId(null);
        }}
        onConfirm={handleConfirmDeleteTask}
        title={t('board.deleteTaskTitle')}
        description={t('board.deleteTaskDescription')}
        confirmLabel={t('common.delete')}
        variant="danger"
        isLoading={isDeleting}
      />

      {/* Board Files Panel (left sidebar) */}
      {boardId && <BoardFilesPanel boardId={boardId} />}

      {/* Board Notes Panel (right sidebar) */}
      {boardId && <BoardNotesPanel boardId={boardId} />}
    </div>
  );
}
